import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { QuizResultFilterType } from '@edtech-ui/constants/quiz-result-filter-type.enum';
import { QuizDataResponse, StudentAttempt, TopPerformer } from '@edtech-ui/core/models/quiz-review-interface.ts/quiz-result';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { PaginatorModule } from 'primeng/paginator';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-students-results',
	standalone: true,
	imports: [CommonModule, FormsModule, PaginatorModule, MatProgressSpinner],
	templateUrl: './students-results.component.html',
	styleUrl: './students-results.component.scss'
})
export class StudentsResultsComponent implements OnInit, OnDestroy {

	subscription = new Subscription();
	_studentQuizResult: StudentAttempt[] = [];
	_paginatedStudents: StudentAttempt[] = [];
	filterValue: QuizResultFilterType = QuizResultFilterType.All;
	_fullStudentQuizResult: QuizDataResponse | null = null;
	_QuizResultFilterType = QuizResultFilterType;
	StudyMaterialTypeEnum = StudyMaterialTypeEnum;

	first: number = 0;
	rows: number = 10;
	_totalRecords: number = 0;
	quizId: string = '';
	classroomId: number = 0;
	isLoading: boolean = false;
	topPerformers: TopPerformer[] = [];

	firstRank: TopPerformer | null = null;
	secondRank: TopPerformer | null = null;
	thirdRank: TopPerformer | null = null;


	constructor(
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private router: Router,
	) { }

	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			this.classroomId = +params['classroomId'];
			this.quizId = params['quizId'];
			this.getAllStudentQuizResult(this.classroomId, this.quizId, 1, this.rows);
		});
	}

	onFilterChange(event: Event): void {
		this.filterValue = (event.target as HTMLSelectElement).value as QuizResultFilterType;
		this.first = 0;
		this.getAllStudentQuizResult(this.classroomId, this.quizId, 1, this.rows, 'name', 'desc', this.filterValue);
	}


	get filteredStudents(): StudentAttempt[] {
		if (!this._studentQuizResult) return [];
		if (this.filterValue === QuizResultFilterType.All) return this._studentQuizResult;
		return this._studentQuizResult.filter(s => s.status.toLowerCase() === this.filterValue.toLowerCase()
		);
	}

	onPageChange(event: any): void {
		this.first = event.first;
		this.rows = event.rows;
		const page = event.page + 1;
		this.getAllStudentQuizResult(this.classroomId, this.quizId, page, this.rows);
	}

	updatePaginatedData(): void {
		this._totalRecords = this.filteredStudents.length;
		const startIndex = this.first;
		const endIndex = startIndex + this.rows;
		this._paginatedStudents = this.filteredStudents.slice(startIndex, endIndex);
	}

	getAllStudentQuizResult(
		classroomId: number,
		quizId: string,
		page: number = 1,
		limit: number = 10,
		sort: string = 'name',
		order: string = 'desc',
		filterType: QuizResultFilterType = QuizResultFilterType.All
	) {
		this.isLoading = true;

		this.subscription.add(
			this._quizService
				.getAllStudentAttemptQuiz(classroomId, quizId, page, limit, sort, order, filterType)
				.subscribe({
					next: (response: any) => {
						this.isLoading = false;
						const data = response?.Data || {};
						this._paginatedStudents = data.data || [];
						this._fullStudentQuizResult = data;
						this._totalRecords = data.totalQuizzes ?? this._paginatedStudents.length;
						this.topPerformers = data.top_performers || [];

						this.firstRank = this.topPerformers.find(p => p.rank === 1) || null;
						this.secondRank = this.topPerformers.find(p => p.rank === 2) || null;
						this.thirdRank = this.topPerformers.find(p => p.rank === 3) || null;

						if (response?.Message) {
							this._toastr.success(response.Message);
						}
					},
					error: (err) => {
						this.isLoading = false;
						this._toastr.error('Failed to fetch student results');
					},
				})
		);
	}


	goToBack() {
		this.router.navigate(['teacher-dashboard/classroom-detail-page', this.classroomId, 'quizzes'])
	}

	convertToIST(utcDateString: string): string {
		if (!utcDateString) return '';
		const date = new Date(utcDateString + 'Z');
		return new Intl.DateTimeFormat('en-IN', {
			timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short'
		}).format(date);
	}

	goToStudentResultPage(attemptId: string) {
		this.router.navigate(['/teacher-dashboard/individual-student-result', attemptId], {
			queryParams: {
				classroomId: this.classroomId, quizId: this.quizId
			}
		});
	}

	formatTime(seconds: number | null | undefined): string {
		if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) {
			return '-';
		}

		if (seconds === 0) {
			return '0 sec';
		}

		const hrs = Math.floor(seconds / 3600);
		const mins = Math.floor((seconds % 3600) / 60);
		const secs = seconds % 60;

		const parts: string[] = [];

		if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
		if (mins > 0) parts.push(`${mins} min${mins > 1 ? 's' : ''}`);
		if (secs > 0) parts.push(`${secs} sec${secs > 1 ? 's' : ''}`);

		return parts.join(' ');
	}



	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}